import { CommonModule } from "@angular/common";
import { Component, ElementRef, Inject, Input, ViewChild } from "@angular/core";
import {
  BehaviorSubject,
  merge,
  filter,
  switchMap,
  mapTo,
  startWith,
  map,
  of,
  combineLatest,
  pairwise,
  scan,
  distinctUntilChanged,
  share,
  fromEvent,
  takeUntil,
  Observable,
  concat,
  endWith,
  first,
  repeat,
  takeWhile,
  Subject,
} from "rxjs";

const EMPTY_COORDINATES: [number, number] = [0, 0];
const DEFAULT_ZOOM = 1;
const MAX_ZOOM = 3;

export const enum DragStage {
  Start,
  Continues,
  End,
}

export class DragState {
  constructor(readonly stage: DragStage, readonly event: MouseEvent) { }
}

export function dragAndDropFrom(element: Element): Observable<DragState> {
  const { ownerDocument } = element;

  if (!ownerDocument) {
    throw new Error("element does not have ownerDocument");
  }

  return concat(
    fromEvent<MouseEvent>(element, "mousedown").pipe(
      first(),
      map((event) => new DragState(DragStage.Start, event))
    ),
    merge(
      fromEvent<MouseEvent>(ownerDocument, "mousemove").pipe(
        map((event) => new DragState(DragStage.Continues, event))
      ),
      merge(
        fromEvent<MouseEvent>(ownerDocument, "mouseup"),
        fromEvent<MouseEvent>(ownerDocument, "dragend")
      ).pipe(
        first(),
        map((event) => new DragState(DragStage.End, event)),
        endWith<DragState | null>(null)
      )
    ).pipe(takeWhile(isPresent))
  ).pipe(repeat());
}

@Component({
  selector: "image-view",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./image-view.component.html",
  styleUrl: "./image-view.component.scss",
})
export class ImageViewComponent {
  @Input() imgSrc: string;

  destroy$ = new Subject();

  readonly drag$ = new Subject<DragState | null>();
  readonly zoom$ = new BehaviorSubject<number>(DEFAULT_ZOOM);
  readonly rotation$ = new BehaviorSubject<number>(0);

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  readonly transitioned$ = merge(
    this.drag$.pipe(
      filter((drag) => drag === null),
      switchMap(() =>
        merge(
          this.zoom$.pipe(filter((zoom) => zoom !== DEFAULT_ZOOM)),
          this.rotation$.pipe(filter((rotation) => rotation !== 0))
        ).pipe(mapTo(true), startWith(false))
      )
    ),
    this.drag$.pipe(
      filter((drag) => drag !== null),
      map((drag) => !drag || drag.stage !== DragStage.Continues)
    ),
    of(true)
  );

  readonly wrapperTranslate$ = combineLatest([
    this.drag$.pipe(startWith(null), pairwise()),
    this.zoom$,
    this.rotation$,
  ]).pipe(
    scan<
      [[DragState | null, DragState | null], number, number],
      [number, number]
    >((coordinates, latest) => {
      const [pair, zoom, rotation] = latest;

      if (pair[1] === null || pair[0] === null) {
        return EMPTY_COORDINATES;
      }

      if (pair[1].stage === DragStage.Start) {
        return coordinates;
      }

      const moveX = pair[1].event.clientX - pair[0].event.clientX;
      const moveY = pair[1].event.clientY - pair[0].event.clientY;

      // ORIGINAL CODE - Only allowed panning when zoomed in (zoom > 1)
      // const offsetX = ((zoom - DEFAULT_ZOOM) * this.imageRef.nativeElement.width) / 2;
      // const offsetY = ((zoom - DEFAULT_ZOOM) * this.imageRef.nativeElement.height) / 2;

      // NEW CODE - Allow panning whenever image doesn't fit in container
      // Calculate if image fits in container
      const zone = this.zoneRef?.nativeElement;
      const img = this.imageRef?.nativeElement;

      let offsetX = 0;
      let offsetY = 0;

      if (zone && img) {
        const zoneWidth = zone.clientWidth;
        const zoneHeight = zone.clientHeight;
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;

        // Calculate scaled image dimensions
        const scaledImgWidth = imgWidth * zoom;
        const scaledImgHeight = imgHeight * zoom;

        // Calculate how much the image extends beyond the container
        const overflowX = Math.max(0, (scaledImgWidth - zoneWidth) / 2);
        const overflowY = Math.max(0, (scaledImgHeight - zoneHeight) / 2);

        // Allow panning if image overflows in either direction
        offsetX = overflowX;
        offsetY = overflowY;
      }

      const x = this.clamp(coordinates[0] + moveX, -offsetX, offsetX);
      const y = this.clamp(coordinates[1] + moveY, -offsetY, offsetY);

      return [x, y];
    }, EMPTY_COORDINATES),
    startWith(EMPTY_COORDINATES),
    map(([x, y]) => `${x}px, ${y}px`),
    distinctUntilChanged(),
    share()
  );

  clamp(num, min, max) {
    return num <= min ? min : num >= max ? max : num;
  }

  readonly wrapperTransform$ = combineLatest([
    this.wrapperTranslate$,
    this.zoom$,
    this.rotation$,
  ]).pipe(
    map(
      ([translate, zoom, rotation]) =>
        `translate(${translate}) scale(${zoom}) rotate(${rotation}deg)`
    )
  );

  @ViewChild("zone")
  zoneRef: ElementRef<HTMLElement>;

  @ViewChild("image")
  imageRef: ElementRef;

  @ViewChild("contentWrapper")
  set contentWrapper(elementRef: ElementRef<HTMLElement> | undefined) {
    if (!elementRef) {
      return;
    }

    merge(
      dragAndDropFrom(elementRef.nativeElement),
      fromEvent<TouchEvent>(elementRef.nativeElement, "touchmove").pipe(
        map(
          (event) =>
            /**
             * DragFrom does not support touches and
             * they are incompatible with MouseEvent, but we may use it
             * while we need only ClientX/Y
             */
            new DragState(DragStage.Continues, event.touches[0] as any)
        )
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.drag$.next(event);
      });
  }

  updateZoom(newZoom: number) {
    this.zoom$.next(this.clamp(newZoom, DEFAULT_ZOOM, MAX_ZOOM));
  }

  reset() {
    this.zoom$.next(DEFAULT_ZOOM);
    this.rotation$.next(0);
    this.drag$.next(null);
  }

  rotate() {
    this.rotation$.next(this.rotation$.value - 90);
  }

  zoomIn() {
    this.updateZoom(this.zoom$.value + 0.5);
  }

  zoomOut() {
    this.updateZoom(this.zoom$.value - 0.5);
  }
}

export function isPresent<T>(value?: T | null): value is T {
  return value !== null && value !== undefined;
}
